import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, ExternalLink } from "lucide-react";

// Types de message pour la communication par départ
const DEP_MSG_TYPES = {
  confirmed: ["Départ confirmé", (d) => `Bonjour,\nVotre envoi Colis2Go vers ${d.dest_country} (${d.mode === "air" ? "aérien" : "maritime"}) a quitté le Hub et est maintenant en route. Merci pour votre confiance.\nColis2Go`],
  delay: ["Retard", (d) => `Bonjour,\nLe départ de votre envoi vers ${d.dest_country} prévu le ${d.departure_date} est légèrement retardé. Nous vous informons dès la nouvelle date confirmée.\nColis2Go`],
  date_change: ["Changement de date", (d) => `Bonjour,\nLa date de départ de votre envoi vers ${d.dest_country} a été modifiée. Nouvelle date : ${d.departure_date}.\nColis2Go`],
  arrival: ["Arrivée prévue", (d) => `Bonjour,\nVotre envoi vers ${d.dest_country} arrive bientôt à destination. Nous vous informerons dès qu'il sera disponible au retrait.\nColis2Go`],
  info: ["Information générale", (d) => `Bonjour,\nInformation concernant votre envoi Colis2Go vers ${d.dest_country} (départ ${d.departure_date}) :\n`],
};

// Écran DÉPARTS PLANIFIÉS : + NOUVEAU DÉPART, liste (trajet, mode, dates, statut, clients),
// communication automatique à tous les clients rattachés à un départ.
export const AdminDepartures = ({ hub = "all" }) => {
  const [deps, setDeps] = useState([]);
  const [opts, setOpts] = useState({ hubs: [], routes: [] });
  const [showForm, setShowForm] = useState(false);
  const [notif, setNotif] = useState(null);
  const [f, setF] = useState({ origin_country: "", dest_country: "", mode: "", departure_date: "", cutoff_date: "" });
  const load = () => api.get(`/admin/departures?hub=${hub}`).then(({ data }) => setDeps(data)).catch(() => {});
  useEffect(() => { load(); }, [hub]); // eslint-disable-line
  useEffect(() => { api.get("/admin/departures/options").then(({ data }) => setOpts(data)).catch(() => {}); }, []);

  const hubCountries = [...new Set(opts.hubs.map((h) => h.country))];
  const hubLabel = (c) => `${c} — ${opts.hubs.filter((h) => h.country === c).map((h) => h.name.replace("Hub Colis2Go ", "")).join(", ")}`;
  const dests = [...new Set(opts.routes.filter((r) => r.origin_country === f.origin_country).map((r) => r.dest_country))];
  const modes = opts.routes.find((r) => r.origin_country === f.origin_country && r.dest_country === f.dest_country)?.modes || [];
  const cutoffFor = (dep) => {
    if (!dep) return "";
    const d = new Date(dep + "T00:00:00");
    d.setDate(d.getDate() - 2);
    return d.toISOString().slice(0, 10);
  };
  const create = async () => {
    try {
      await api.post("/admin/departures", f);
      toast.success("Départ planifié créé");
      setF({ origin_country: "", dest_country: "", mode: "", departure_date: "", cutoff_date: "" });
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const statusBadge = (d) => d.processed
    ? <Badge className="rounded-sm bg-emerald-600 text-white">Clôturé</Badge>
    : <Badge className={`rounded-sm ${d.active ? "bg-blue-100 text-blue-700" : "bg-secondary"}`}>{d.active ? "Ouvert" : "Inactif"}</Badge>;

  return (
    <div data-testid="departures-screen">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Départs planifiés</h2>
        <Button data-testid="dep-new-btn" className="rounded-sm bg-accent text-white gap-1" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4" /> Nouveau départ
        </Button>
      </div>

      {showForm && (
        <div className="mt-3 border border-border bg-card p-4" data-testid="departures-panel">
          <div className="grid gap-2 grid-cols-2 md:grid-cols-6 items-end">
            <div><Label className="text-xs">Hub (pays)</Label>
              <select data-testid="dep-origin" className="w-full h-8 border border-border bg-card px-1 text-sm" value={f.origin_country}
                onChange={(e) => setF({ ...f, origin_country: e.target.value, dest_country: "", mode: "" })}>
                <option value="">— Hub actif —</option>
                {hubCountries.map((c) => <option key={c} value={c}>{hubLabel(c)}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Destination</Label>
              <select data-testid="dep-dest" className="w-full h-8 border border-border bg-card px-1 text-sm" value={f.dest_country} disabled={!f.origin_country}
                onChange={(e) => setF({ ...f, dest_country: e.target.value, mode: "" })}>
                <option value="">{f.origin_country ? "— Destination —" : "Choisir le Hub d'abord"}</option>
                {dests.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Mode</Label>
              <select data-testid="dep-mode" className="w-full h-8 border border-border bg-card px-1 text-sm" value={f.mode} disabled={!f.dest_country}
                onChange={(e) => setF({ ...f, mode: e.target.value })}>
                <option value="">{f.dest_country ? "— Mode —" : "Choisir la destination"}</option>
                {modes.map((m) => <option key={m} value={m}>{m === "air" ? "Aérien / Express" : "Maritime"}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Départ</Label><Input data-testid="dep-date" type="date" className="rounded-sm h-8" value={f.departure_date}
              onChange={(e) => setF({ ...f, departure_date: e.target.value, cutoff_date: cutoffFor(e.target.value) })} /></div>
            <div><Label className="text-xs">Clôture (départ − 2 j)</Label><Input data-testid="dep-cutoff" type="date" className="rounded-sm h-8" value={f.cutoff_date} onChange={(e) => setF({ ...f, cutoff_date: e.target.value })} /></div>
            <Button size="sm" data-testid="dep-create" className="rounded-sm bg-accent text-white h-8"
              disabled={!f.origin_country || !f.dest_country || !f.mode || !f.departure_date} onClick={create}>Ajouter</Button>
          </div>
        </div>
      )}

      <div className="mt-4 border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Trajet</th><th className="px-3 py-2">Mode</th>
              <th className="px-3 py-2">Départ</th><th className="px-3 py-2">Clôture</th>
              <th className="px-3 py-2">Statut</th><th className="px-3 py-2">Clients</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {deps.map((d) => (
              <tr key={d.id} data-testid={`dep-row-${d.id}`} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-semibold whitespace-nowrap">{d.origin_country} → {d.dest_country}</td>
                <td className="px-3 py-2">{d.mode === "air" ? "Aérien" : "Maritime"}</td>
                <td className="px-3 py-2 font-bold whitespace-nowrap">{d.departure_date}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{d.cutoff_date}</td>
                <td className="px-3 py-2">{statusBadge(d)}</td>
                <td className="px-3 py-2"><Badge className="rounded-sm bg-secondary text-primary">{d.clients_count || 0} client(s) · {d.shipments_count || 0} colis</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1.5">
                    {(d.clients_count || 0) > 0 && (
                      <Button size="sm" data-testid={`dep-notify-${d.id}`} className="rounded-sm h-7 text-xs bg-primary text-white gap-1"
                        onClick={() => setNotif(d)}><Send className="h-3 w-3" /> Informer les clients</Button>
                    )}
                    {!d.processed && (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid={`dep-toggle-${d.id}`}
                        onClick={async () => { await api.put(`/admin/departures/${d.id}`, { active: !d.active }); load(); }}>
                        {d.active ? "Désactiver" : "Activer"}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {deps.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Aucun départ planifié.</td></tr>}
          </tbody>
        </table>
      </div>

      {notif && <DepNotifyDialog dep={notif} onClose={() => setNotif(null)} />}
    </div>
  );
};

// Communication par départ : identifie automatiquement les clients rattachés — jamais de copie manuelle.
function DepNotifyDialog({ dep, onClose }) {
  const [type, setType] = useState("confirmed");
  const [msg, setMsg] = useState(DEP_MSG_TYPES.confirmed[1](dep));
  const [ch, setCh] = useState({ whatsapp: true, email: true });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const pick = (k) => { setType(k); setMsg(DEP_MSG_TYPES[k][1](dep)); };
  const send = async () => {
    const channels = Object.keys(ch).filter((k) => ch[k]);
    if (!channels.length) return toast.error("Choisissez au moins un canal");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/departures/${dep.id}/notify`, {
        channels, message: msg, subject_type: type,
        subject: `${DEP_MSG_TYPES[type][0]} — ${dep.origin_country} → ${dep.dest_country} (${dep.departure_date})`,
      });
      setResult(data);
      toast.success(`${data.clients} client(s) informé(s)${data.emails_sent ? ` · ${data.emails_sent} email(s) envoyé(s)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="dep-notify-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Informer les clients de ce départ</DialogTitle>
          <DialogDescription>Tous les clients rattachés à ce départ sont identifiés automatiquement.</DialogDescription></DialogHeader>
        <div className="text-sm">
          <b>{dep.origin_country} → {dep.dest_country}</b> · {dep.mode === "air" ? "Aérien" : "Maritime"} · Départ {dep.departure_date}
          <Badge className="ml-2 rounded-sm bg-accent text-white">{dep.clients_count} client(s) concerné(s)</Badge>
        </div>
        {!result ? (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Type de message</Label>
                <select data-testid="depnotif-type" className="w-full h-9 border border-border bg-card px-2 text-sm" value={type} onChange={(e) => pick(e.target.value)}>
                  {Object.entries(DEP_MSG_TYPES).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <Checkbox data-testid="depnotif-whatsapp" checked={ch.whatsapp} onCheckedChange={(v) => setCh({ ...ch, whatsapp: !!v })} /> WhatsApp
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <Checkbox data-testid="depnotif-email" checked={ch.email} onCheckedChange={(v) => setCh({ ...ch, email: !!v })} /> Email
                </label>
              </div>
              <div>
                <Label className="text-xs">Message (modifiable)</Label>
                <textarea data-testid="depnotif-message" className="w-full min-h-[110px] border border-border bg-card p-2 text-sm" value={msg} onChange={(e) => setMsg(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="depnotif-send" disabled={busy} className="rounded-sm bg-accent text-white gap-1.5" onClick={send}>
                <Send className="h-4 w-4" /> Envoyer
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-3" data-testid="depnotif-result">
            <p className="text-sm font-semibold text-emerald-700">
              ✓ {result.clients} client(s) notifié(s) dans l'app{result.emails_sent > 0 && <> · {result.emails_sent} email(s) envoyé(s)</>}
            </p>
            {(result.whatsapp_links || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold">WhatsApp — cliquez pour envoyer le message prérempli :</p>
                <div className="mt-1.5 space-y-1 max-h-52 overflow-y-auto">
                  {result.whatsapp_links.map((w, i) => (
                    <a key={i} href={w.link} target="_blank" rel="noreferrer" data-testid={`depnotif-wa-${i}`}
                      className="flex items-center justify-between border border-border px-2.5 py-1.5 text-sm hover:border-accent">
                      <span>{w.name} <span className="text-muted-foreground text-xs">({w.number})</span></span>
                      <ExternalLink className="h-3.5 w-3.5 text-accent" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter><Button variant="outline" className="rounded-sm" onClick={onClose}>Fermer</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
